import { Button, Card, cn } from '@rfz/ui';
import { AlertTriangle, Info, Loader2, RefreshCw, TrendingUp } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { SeasonalityEstimate } from './api.js';
import {
  defaultSeasonalityDisplayMode,
  type SeasonalityDisplayMode as DisplayMode,
} from './seasonality-ui.js';

function formatNumber(value: number | null) {
  return value == null ? '—' : Math.round(value).toLocaleString('zh-CN');
}

function confidenceLabel(value: SeasonalityEstimate['confidence']) {
  return value === 'high' ? '高' : value === 'medium' ? '中' : '低';
}

function entityRoleLabel(value: string | null) {
  const labels: Record<string, string> = {
    product: '商品',
    subtype: '商品子类',
    component: '部件',
    accessory: '配件',
    consumable: '耗材',
    material: '材料',
  };
  return value ? (labels[value] ?? value) : '暂无';
}

function SeasonalityChart({
  estimate,
  mode,
}: {
  estimate: SeasonalityEstimate;
  mode: DisplayMode;
}) {
  const [activeMonth, setActiveMonth] = useState<number | null>(null);
  const geometry = useMemo(() => {
    const width = 1200;
    const height = 320;
    const left = 58;
    const right = 22;
    const top = 24;
    const bottom = 54;
    const chartWidth = width - left - right;
    const chartHeight = height - top - bottom;
    const values = estimate.months.map((month) =>
      mode === 'volume' ? (month.estimatedVolume ?? 0) : month.index,
    );
    const maximum = mode === 'index' ? 100 : Math.max(1, ...values);
    const yMaximum = mode === 'index' ? 100 : Math.ceil((maximum * 1.1) / 10) * 10;
    const points = values.map((value, index) => ({
      x: left + (chartWidth * index) / 11,
      y: top + chartHeight * (1 - value / yMaximum),
      value,
      month: estimate.months[index]!,
    }));
    return {
      width,
      height,
      left,
      top,
      chartHeight,
      yMaximum,
      points,
      path: points.map((point, index) => `${index ? 'L' : 'M'} ${point.x} ${point.y}`).join(' '),
      area: `${points.map((point, index) => `${index ? 'L' : 'M'} ${point.x} ${point.y}`).join(' ')} L ${points.at(-1)?.x ?? 0} ${top + chartHeight} L ${points[0]?.x ?? 0} ${top + chartHeight} Z`,
    };
  }, [estimate, mode]);
  const active = activeMonth == null ? null : geometry.points[activeMonth];

  return (
    <div className="seasonality-chart-shell">
      <svg
        className="seasonality-chart"
        viewBox={`0 0 ${geometry.width} ${geometry.height}`}
        role="img"
        aria-label={`${estimate.keywordRu} 12个月季节性估算曲线`}
      >
        <defs>
          <linearGradient id="seasonality-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.22" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = geometry.top + geometry.chartHeight * ratio;
          const value = Math.round(geometry.yMaximum * (1 - ratio));
          return (
            <g key={ratio}>
              <line className="seasonality-grid-line" x1={geometry.left} x2="1178" y1={y} y2={y} />
              <text className="seasonality-axis-label" x="48" y={y + 4} textAnchor="end">
                {value.toLocaleString('zh-CN')}
              </text>
            </g>
          );
        })}
        <path className="seasonality-area" d={geometry.area} />
        <path className="seasonality-line" d={geometry.path} />
        {geometry.points.map((point, index) => (
          <g key={point.month.monthKey}>
            {point.month.currentAnchor && (
              <line
                className="seasonality-anchor-line"
                x1={point.x}
                x2={point.x}
                y1={geometry.top}
                y2={geometry.top + geometry.chartHeight}
              />
            )}
            <circle
              className={cn(
                'seasonality-point',
                point.month.currentAnchor && 'is-current',
                activeMonth === index && 'is-active',
              )}
              cx={point.x}
              cy={point.y}
              r={point.month.currentAnchor ? 6 : 5}
              tabIndex={0}
              onMouseEnter={() => setActiveMonth(index)}
              onMouseLeave={() => setActiveMonth(null)}
              onFocus={() => setActiveMonth(index)}
              onBlur={() => setActiveMonth(null)}
            >
              <title>
                {point.month.label}：季节指数 {point.month.index}，估算搜索量{' '}
                {formatNumber(point.month.estimatedVolume)}
              </title>
            </circle>
            <text className="seasonality-month-label" x={point.x} y="300" textAnchor="middle">
              {point.month.label}
            </text>
          </g>
        ))}
      </svg>
      {active && (
        <div
          className="seasonality-tooltip"
          style={{ left: `${Math.min(92, Math.max(8, (active.x / geometry.width) * 100))}%` }}
        >
          <strong>{active.month.label}</strong>
          <span>季节指数：{active.month.index}</span>
          <span>估算搜索量：{formatNumber(active.month.estimatedVolume)}</span>
          {active.month.currentAnchor && <em>当前频次锚点</em>}
          <small>数据类型：模型估算</small>
        </div>
      )}
    </div>
  );
}

export function SeasonalityEstimatePanel({
  keyword,
  estimate,
  loading,
  refreshing,
  autoRefreshNotice,
  onRefresh,
}: {
  keyword: string;
  estimate: SeasonalityEstimate | undefined;
  loading: boolean;
  refreshing: boolean;
  autoRefreshNotice?: string | null;
  onRefresh: () => void;
}) {
  const [displayMode, setDisplayMode] = useState<DisplayMode>('index');
  const displayDefaultKey = useRef<string | null>(null);

  useEffect(() => {
    if (!estimate) return;
    const nextKey = [
      estimate.keywordRu,
      estimate.currentPhraseVolume ?? 'missing',
      estimate.templateVersion,
      estimate.calculatedAt,
    ].join('|');
    if (displayDefaultKey.current === nextKey) return;
    displayDefaultKey.current = nextKey;
    setDisplayMode(defaultSeasonalityDisplayMode(estimate.volumeAvailable));
  }, [estimate]);

  return (
    <Card className="seasonality-panel" data-testid="seasonality-panel">
      <header className="seasonality-header">
        <div>
          <p className="eyebrow">SEASONALITY ESTIMATE</p>
          <h3>12个月季节性估算</h3>
          <p>
            依据当前Yandex Wordstat频次、Yandex
            Suggest季节信号、DeepSeek俄罗斯市场假设和商品类目模型推算，仅供选品参考，不代表历史月度搜索量。
          </p>
        </div>
        <Button variant="ghost" onClick={onRefresh} disabled={refreshing || !keyword}>
          <RefreshCw size={15} className={refreshing ? 'spin' : undefined} />
          重新计算
        </Button>
      </header>

      {loading && !estimate ? (
        <div className="seasonality-loading">
          <Loader2 className="spin" size={20} /> 正在生成季节性估算…
        </div>
      ) : estimate ? (
        <>
          {estimate.fallbackMessage && (
            <div className="seasonality-fallback" role="status">
              <AlertTriangle size={17} /> {estimate.fallbackMessage}
            </div>
          )}
          {autoRefreshNotice && (
            <div className="seasonality-auto-refresh-notice" role="status">
              <RefreshCw size={15} /> {autoRefreshNotice}
            </div>
          )}
          <div className="seasonality-metrics">
            <div>
              <small>当前商品主词</small>
              <strong>{estimate.keywordRu}</strong>
            </div>
            <div>
              <small>当前地区</small>
              <strong>{estimate.region}</strong>
            </div>
            <div>
              <small>季节类型</small>
              <strong>{estimate.seasonType}</strong>
            </div>
            <div>
              <small>预计旺季</small>
              <strong>{estimate.expectedSeason}</strong>
            </div>
            <div>
              <small>当前Wordstat频次</small>
              <strong>{formatNumber(estimate.currentPhraseVolume)}</strong>
            </div>
            <div>
              <small>预计全年波动</small>
              <strong>{estimate.annualVariationLabel}</strong>
            </div>
            <div>
              <small>估算可信度</small>
              <strong>{confidenceLabel(estimate.confidence)}</strong>
            </div>
          </div>

          <div className="seasonality-chart-toolbar">
            <div className="seasonality-toggle" role="group" aria-label="趋势图数据类型">
              <button
                type="button"
                className={displayMode === 'index' ? 'is-active' : undefined}
                onClick={() => setDisplayMode('index')}
              >
                季节指数
              </button>
              <button
                type="button"
                className={displayMode === 'volume' ? 'is-active' : undefined}
                disabled={!estimate.volumeAvailable}
                onClick={() => setDisplayMode('volume')}
              >
                估算搜索量
              </button>
            </div>
            <span>
              {estimate.cache.hit ? '已使用24小时缓存' : '最新计算'} · Suggest{' '}
              {estimate.suggest.evidenceCount}条有效信号
              {estimate.aiHypothesis ? ' · DeepSeek 已参与' : ''}
            </span>
          </div>
          {!estimate.volumeAvailable && (
            <p className="seasonality-volume-unavailable" role="status">
              当前Wordstat频次为空、失败或为0，因此仅展示季节指数，估算搜索量暂不可用。
            </p>
          )}
          <SeasonalityChart estimate={estimate} mode={displayMode} />
          <details className="seasonality-explanation">
            <summary>
              <Info size={14} /> 为什么这样判断
            </summary>
            <dl className="seasonality-decision-basis">
              <div>
                <dt>商品实体</dt>
                <dd>{estimate.decisionBasis.productEntity}</dd>
              </div>
              <div>
                <dt>中文商品</dt>
                <dd>{estimate.decisionBasis.standardProductNameZh ?? '暂无'}</dd>
              </div>
              <div>
                <dt>商品类别</dt>
                <dd>{estimate.decisionBasis.categoryLabel}</dd>
              </div>
              <div>
                <dt>商品角色</dt>
                <dd>{entityRoleLabel(estimate.decisionBasis.entityRole)}</dd>
              </div>
              <div>
                <dt>采用模板</dt>
                <dd>{estimate.decisionBasis.templateId}</dd>
              </div>
              <div>
                <dt>有效Suggest</dt>
                <dd>{estimate.decisionBasis.validSuggestCount}</dd>
              </div>
              <div>
                <dt>强季节信号</dt>
                <dd>
                  {estimate.decisionBasis.matchedSeasonSignals.length
                    ? estimate.decisionBasis.matchedSeasonSignals.join('、')
                    : '0'}
                </dd>
              </div>
              <div>
                <dt>冲突信号</dt>
                <dd>
                  {estimate.decisionBasis.conflictingSeasonSignals.length
                    ? estimate.decisionBasis.conflictingSeasonSignals.join('、')
                    : '0'}
                </dd>
              </div>
              <div>
                <dt>当前Wordstat频次</dt>
                <dd>{formatNumber(estimate.decisionBasis.currentPhraseVolume)}</dd>
              </div>
              <div>
                <dt>DeepSeek季节假设</dt>
                <dd>
                  {estimate.decisionBasis.aiTemplateId
                    ? `${estimate.decisionBasis.aiTemplateId}（可信度 ${Math.round(
                        (estimate.decisionBasis.aiConfidence ?? 0) * 100,
                      )}%）`
                    : '未使用'}
                </dd>
              </div>
              <div>
                <dt>AI参考旺季</dt>
                <dd>
                  {estimate.decisionBasis.aiPeakMonths.length
                    ? estimate.decisionBasis.aiPeakMonths
                        .map((month) => `${month + 1}月`)
                        .join('、')
                    : '无明确峰值'}
                </dd>
              </div>
              <div>
                <dt>AI判断依据</dt>
                <dd>{estimate.decisionBasis.aiReasonZh ?? '暂无'}</dd>
              </div>
              <div>
                <dt>可信度原因</dt>
                <dd>{estimate.decisionBasis.confidenceReason}</dd>
              </div>
              <div>
                <dt>最后计算时间</dt>
                <dd>{new Date(estimate.calculatedAt).toLocaleString('zh-CN')}</dd>
              </div>
              <div>
                <dt>结论</dt>
                <dd>{estimate.decisionBasis.conclusion}</dd>
              </div>
            </dl>
          </details>
          <p className="seasonality-source-note">
            <TrendingUp size={14} />
            本图依据当前Wordstat频次、Yandex Suggest季节信号
            {estimate.aiHypothesis ? '、DeepSeek俄罗斯市场假设' : ''}
            和商品类目模型推算，仅供选品参考，不代表Yandex历史月度搜索量。
          </p>
        </>
      ) : (
        <div className="seasonality-loading">暂时无法生成季节性估算，不影响关键词结果。</div>
      )}
    </Card>
  );
}
